import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Heading, Stat } from "./NKDaily";
import { CountryFlag, GlobeInline, AvatarIcon } from "@/lib/identity";
import { MagnifyingGlass, ArrowSquareOut, ChartLine, X } from "@phosphor-icons/react";

export default function CountryAnalytics() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [region, setRegion] = useState("ALL");
  const [historyCode, setHistoryCode] = useState(null);

  useEffect(() => {
    api.get("/admin/countries/analytics")
      .then((r) => setRows(r.data?.rows || []))
      .catch(() => setRows([]));
  }, []);

  const filtered = useMemo(() => {
    let r = rows;
    if (region !== "ALL") r = r.filter((c) => c.region === region);
    if (q.trim()) {
      const n = q.trim().toLowerCase();
      r = r.filter((c) => c.country_name.toLowerCase().includes(n) || c.country_code.toLowerCase().includes(n));
    }
    return [...r].sort((a, b) => (b.total_players || 0) - (a.total_players || 0));
  }, [rows, q, region]);

  const totals = useMemo(() => ({
    totalPlayers: rows.reduce((s, r) => s + (r.total_players || 0), 0),
    rankedPlayers: rows.reduce((s, r) => s + (r.ranked_players || 0), 0),
    countriesLive: rows.filter((r) => (r.total_players || 0) > 0).length,
  }), [rows]);

  return (
    <div className="space-y-6">
      <Heading title="Country Analytics" subtitle="Growth + activity per country. Use this to spot the next playoff candidate." />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Countries seeded" value={rows.length} />
        <Stat label="Countries with players" value={totals.countriesLive} />
        <Stat label="Total players" value={totals.totalPlayers.toLocaleString()} />
        <Stat label="Ranked players (gated)" value={totals.rankedPlayers.toLocaleString()} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 border border-nk-line px-3 py-2 rounded-sm bg-nk-surface flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="text-nk-mute" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
                 data-testid="country-analytics-search"
                 className="flex-1 bg-transparent text-sm outline-none placeholder:text-nk-dim" />
        </div>
        <select value={region} onChange={(e) => setRegion(e.target.value)}
                data-testid="country-analytics-region"
                className="bg-nk-surface border border-nk-line px-3 py-2 text-sm rounded-sm">
          <option value="ALL">All regions</option>
          <option value="West">West</option><option value="East">East</option>
          <option value="Central">Central</option><option value="North">North</option>
          <option value="Southern">Southern</option><option value="ROW">ROW</option>
        </select>
      </div>

      <div className="nk-card overflow-x-auto" data-testid="country-analytics-table">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="border-b border-nk-line">
            <tr>
              <Th>Country</Th><Th>Status</Th>
              <Th right>Total</Th><Th right>Ranked</Th>
              <Th right>Country Score</Th><Th>Top player</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.country_code} className="border-b border-nk-line/40 last:border-0">
                <td className="px-4 py-2.5 flex items-center gap-2.5">
                  {c.country_code === "ROW" ? <GlobeInline size={16} /> : <CountryFlag country={c.country_code} size={18} />}
                  <span className="font-medium">{c.country_name}</span>
                  <span className="text-[10px] font-mono text-nk-dim">{c.country_code}</span>
                </td>
                <td className="px-3 py-2.5">
                  {c.playoff_eligible ? (
                    <span className="px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider rounded-sm bg-nk-gold/15 text-nk-gold border border-nk-gold/30">Playoff</span>
                  ) : c.is_african ? (
                    <span className="px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider rounded-sm bg-nk-accent/15 text-nk-accent border border-nk-accent/30">Ranked</span>
                  ) : (
                    <span className="px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider rounded-sm bg-nk-line text-nk-mute">ROW</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right font-mono">{(c.total_players || 0).toLocaleString()}</td>
                <td className="px-3 py-2.5 text-right font-mono text-nk-accent">{(c.ranked_players || 0).toLocaleString()}</td>
                <td className="px-3 py-2.5 text-right font-mono text-nk-gold">{(c.country_score || 0).toLocaleString()}</td>
                <td className="px-3 py-2.5 text-xs text-nk-mute">
                  {c.top_player ? (
                    <Link to={`/profile/${c.top_player.user_id}`} className="inline-flex items-center gap-2 hover:text-nk-text">
                      <AvatarIcon id={c.top_player.avatar} size={18} />
                      <span>{c.top_player.username}</span>
                    </Link>
                  ) : "—"}
                </td>
                <td className="px-3 py-2.5 text-right">
                  <div className="inline-flex items-center gap-2">
                    <button onClick={() => setHistoryCode(c.country_code)}
                            data-testid={`country-analytics-history-${c.country_code.toLowerCase()}`}
                            title="View score history"
                            className="inline-flex items-center gap-1 text-[11px] text-nk-mute hover:text-nk-gold">
                      <ChartLine size={11} />
                    </button>
                    <Link to={`/country/${c.country_code}`}
                          data-testid={`country-analytics-open-${c.country_code.toLowerCase()}`}
                          className="inline-flex items-center gap-1 text-[11px] text-nk-mute hover:text-nk-accent">
                      Open <ArrowSquareOut size={11} />
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-nk-dim">No countries match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {historyCode && (
        <HistoryModal code={historyCode} onClose={() => setHistoryCode(null)}
                      countryName={rows.find((r) => r.country_code === historyCode)?.country_name} />
      )}
    </div>
  );
}

function HistoryModal({ code, countryName, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/countries/${code}/score-history?days=30`)
      .then((r) => setData(r.data))
      .catch(() => setData({ series: [], total_in_window: 0, peak_day_score: 0 }));
  }, [code]);

  const w = 640, h = 180, pad = 12;
  let path = "", barW = 0, peak = 1;
  if (data && data.series.length) {
    peak = Math.max(1, data.peak_day_score);
    barW = (w - pad * 2) / data.series.length;
    path = data.series.map((d, i) => {
      const x = pad + i * barW + barW / 2;
      const y = h - pad - ((d.score_added / peak) * (h - pad * 2));
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    }).join(" ");
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="nk-card-gold w-full max-w-3xl p-5 rounded-sm" data-testid="country-history-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="eyebrow text-nk-gold">Score history · last 30 days</p>
            <p className="font-display text-lg mt-1">{countryName} <span className="text-nk-dim text-xs font-mono">{code}</span></p>
          </div>
          <button onClick={onClose} className="text-nk-mute hover:text-nk-text"><X size={14} /></button>
        </div>

        {!data ? (
          <p className="text-nk-mute text-sm py-10 text-center">Loading…</p>
        ) : data.series.length === 0 ? (
          <p className="text-nk-dim text-sm py-10 text-center">No Season Score earned in this country over the last 30 days yet.</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <Stat label="Total SS added" value={data.total_in_window.toLocaleString()} />
              <Stat label="Peak day" value={data.peak_day_score.toLocaleString()} />
              <Stat label="Active days" value={data.series.length} />
            </div>
            <svg viewBox={`0 0 ${w} ${h}`} className="w-full border border-nk-line/40 rounded-sm bg-nk-bg/40">
              {/* Baseline */}
              <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad} stroke="#26262B" strokeWidth="0.5" />
              {/* Bars */}
              {data.series.map((d, i) => {
                const barH = (d.score_added / peak) * (h - pad * 2);
                const x = pad + i * barW + 1;
                return (
                  <rect key={d.date} x={x} y={h - pad - barH}
                        width={Math.max(1, barW - 2)} height={barH}
                        fill="#D4AF37" opacity="0.45" />
                );
              })}
              {/* Line */}
              <path d={path} stroke="#D4AF37" strokeWidth="1.5" fill="none" />
            </svg>
            <p className="text-[10px] text-nk-dim mt-2">
              {data.series[0].date} → {data.series[data.series.length - 1].date}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function Th({ children, right = false }) {
  return <th className={`px-4 py-3 text-xs text-nk-mute font-medium uppercase tracking-wider ${right ? "text-right" : "text-left"}`}>{children}</th>;
}
